const MODERN_PRODUCT_SELECTOR = '[data-product-modern]';
const PRODUCT_STORY_SELECTOR = '[data-product-story]';
const PRODUCT_STORY_NAV_SELECTOR = '[data-product-story-nav]';

function initialiseModernProduct(root) {
  if (root.dataset.modernProductInitialised === 'true') return;

  root.dataset.modernProductInitialised = 'true';
  root.dataset.motion = 'ready';

  requestAnimationFrame(() => {
    requestAnimationFrame(() => root.classList.add('is-ready'));
  });

  if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
    const gallery = root.querySelector('media-gallery');
    const gallerySurface = gallery?.querySelector('slideshow-container');

    if (gallery && gallerySurface) {
      let pointerFrame = 0;
      let pointerX = 0;
      let pointerY = 0;

      const renderPointerPosition = () => {
        gallery.style.setProperty('--gallery-pointer-x', `${pointerX.toFixed(2)}px`);
        gallery.style.setProperty('--gallery-pointer-y', `${pointerY.toFixed(2)}px`);
        pointerFrame = 0;
      };

      gallerySurface.addEventListener('pointermove', (event) => {
        const bounds = gallerySurface.getBoundingClientRect();
        const x = Math.max(-0.5, Math.min(0.5, (event.clientX - bounds.left) / bounds.width - 0.5));
        const y = Math.max(-0.5, Math.min(0.5, (event.clientY - bounds.top) / bounds.height - 0.5));

        pointerX = x * 16;
        pointerY = y * 12;
        gallery.dataset.galleryPointerActive = 'true';

        if (!pointerFrame) pointerFrame = requestAnimationFrame(renderPointerPosition);
      });

      gallerySurface.addEventListener('pointerleave', () => {
        if (pointerFrame) cancelAnimationFrame(pointerFrame);
        pointerFrame = 0;
        pointerX = 0;
        pointerY = 0;
        gallery.removeAttribute('data-gallery-pointer-active');
        renderPointerPosition();
      });
    }
  }
}

function initialiseAllModernProducts(scope = document) {
  if (scope instanceof Element && scope.matches(MODERN_PRODUCT_SELECTOR)) {
    initialiseModernProduct(scope);
  }

  scope.querySelectorAll(MODERN_PRODUCT_SELECTOR).forEach(initialiseModernProduct);
}

function initialiseProductStory(section) {
  if (section.dataset.productStoryInitialised === 'true') return;

  section.dataset.productStoryInitialised = 'true';

  const revealItems = section.querySelectorAll('[data-story-reveal]');
  if (!revealItems.length || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    revealItems.forEach((item) => item.classList.add('is-visible'));
    return;
  }

  section.dataset.storyMotion = 'ready';
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;

        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.12 }
  );

  revealItems.forEach((item) => observer.observe(item));
}

function initialiseProductStoryNav(nav) {
  if (nav.dataset.productStoryNavInitialised === 'true') return;

  nav.dataset.productStoryNavInitialised = 'true';
  const links = [...nav.querySelectorAll('[data-story-nav-link]')];
  const navScroller = nav.querySelector('.fx-product-nav__inner');
  const mobileNavigation = window.matchMedia('(max-width: 749px)');
  const targets = links
    .map((link) => {
      const id = link.getAttribute('href')?.slice(1);
      const target = id ? document.getElementById(id) : null;
      return target ? { link, target } : null;
    })
    .filter(Boolean);

  if (!targets.length) return;

  let updateScheduled = false;
  let mobileRailPrepared = false;

  const prepareMobileRail = () => {
    if (!(navScroller instanceof HTMLElement)) return;

    if (mobileNavigation.matches && !mobileRailPrepared) {
      navScroller.scrollLeft = Math.max(0, navScroller.clientWidth / 2 - 16);
      mobileRailPrepared = true;
    } else if (!mobileNavigation.matches && mobileRailPrepared) {
      navScroller.scrollLeft = 0;
      mobileRailPrepared = false;
    }
  };

  const centreMobileLink = (link) => {
    if (!mobileNavigation.matches || !(navScroller instanceof HTMLElement)) return;

    const scrollerRect = navScroller.getBoundingClientRect();
    const linkRect = link.getBoundingClientRect();
    const targetLeft =
      navScroller.scrollLeft + linkRect.left + linkRect.width / 2 - scrollerRect.left - scrollerRect.width / 2;

    navScroller.scrollTo({ left: targetLeft, behavior: 'smooth' });
  };

  links.forEach((link) => {
    link.addEventListener('click', () => centreMobileLink(link));
  });

  const updateActiveLink = () => {
    updateScheduled = false;
    const activationLine = Math.min(window.innerHeight * 0.34, 320);
    let activeTarget = targets[0];

    targets.forEach((candidate) => {
      if (candidate.target.getBoundingClientRect().top <= activationLine) activeTarget = candidate;
    });

    links.forEach((link) => link.removeAttribute('aria-current'));
    activeTarget.link.setAttribute('aria-current', 'true');
  };

  const scheduleUpdate = () => {
    if (updateScheduled) return;

    updateScheduled = true;
    requestAnimationFrame(updateActiveLink);
  };

  window.addEventListener('scroll', scheduleUpdate, { passive: true });
  window.addEventListener(
    'resize',
    () => {
      prepareMobileRail();
      scheduleUpdate();
    },
    { passive: true }
  );
  prepareMobileRail();
  updateActiveLink();
}

function initialiseAllProductStoryComponents(scope = document) {
  if (scope instanceof Element && scope.matches(PRODUCT_STORY_SELECTOR)) initialiseProductStory(scope);
  if (scope instanceof Element && scope.matches(PRODUCT_STORY_NAV_SELECTOR)) initialiseProductStoryNav(scope);

  scope.querySelectorAll(PRODUCT_STORY_SELECTOR).forEach(initialiseProductStory);
  scope.querySelectorAll(PRODUCT_STORY_NAV_SELECTOR).forEach(initialiseProductStoryNav);
}

initialiseAllModernProducts();
initialiseAllProductStoryComponents();

document.addEventListener('shopify:section:load', (event) => {
  initialiseAllModernProducts(event.target);
  initialiseAllProductStoryComponents(event.target);
});
